import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react';
import { gallerySlides, insideItems } from '../data/gallery';
const easeOut = [0.23, 1, 0.32, 1] as const;
export function ProductGallery() {
  const [[index, direction], setState] = useState<[number, number]>([0, 0]);
  const [isZoomed, setIsZoomed] = useState(false);
  const slide = gallerySlides[index];
  const paginate = useCallback((step: number) => {
    setState(([current]) => {
      const next = (current + step + gallerySlides.length) % gallerySlides.length;
      return [next, step];
    });
  }, []);
  const goTo = useCallback((next: number) => {
    setState(([current]) => [next, next > current ? 1 : -1]);
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') paginate(1);
      if (event.key === 'ArrowLeft') paginate(-1);
      if (event.key === 'Escape') setIsZoomed(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [paginate]);
  return <section className="py-20 px-4 bg-white" data-id="element-176" data-mp="3dq779mchykat">
      <div className="max-w-5xl mx-auto" data-id="element-177" data-mp="3dpxe5pw5nx0w">
        <div className="text-center mb-10" data-id="element-178" data-mp="38rcogkej4vqp">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-3 text-[#081632]" data-id="element-179" data-mp="38snsmlbw05ic">
            See Inside Judo Coach Ready
          </h2>
          <p className="text-lg text-slate-700 max-w-3xl mx-auto" data-id="element-180" data-mp="2kzfbgpzcfxo0">
            Real pages from the system — visual technical progressions, ready-to-use drills,
            complete session structures and professional coaching tools.
          </p>
        </div>

        <div className="grid md:grid-cols-[1fr_1.2fr] gap-6 md:gap-10 items-start mb-12" data-id="element-181" data-mp="2q035zlms6wmz">
          <div data-id="element-182" data-mp="229zc45zeab96">
            <p className="text-xl font-serif font-bold text-[#081632] mb-3" data-id="element-183" data-mp="229phg53enfx5">
              Judo Coach Ready is a{' '}
              <span className="text-[#D89B16]" data-id="element-184" data-mp="1xa4i3tkbp5jw">highly visual</span> PDF-based coaching system.
            </p>
            <p className="text-slate-700 leading-relaxed" data-id="element-185" data-mp="1785ibcwc4np">
              Instead of relying on long pages of theory, the material uses visual resources
              throughout the 6-in-1 system to make technical concepts easier to understand and apply
              on the tatami.
            </p>
          </div>
          <div className="bg-[#F5F7FB] border border-[#D89B16]/40 rounded-2xl p-6" data-id="element-186" data-mp="3efuh0mcw9a3n">
            <p className="text-sm font-bold text-[#1E4ED8] uppercase tracking-wider mb-4" data-id="element-187" data-mp="3ehxetskag7ix">
              Inside you’ll find:
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2.5" data-id="element-188" data-mp="3ocpf545998he">
              {insideItems.map(item => <li key={item} className="flex items-start gap-2 text-sm text-slate-700" data-id="element-189" data-mp="3sg589qi48qpc">
                  <CheckCircle2 size={16} className="text-[#D89B16] shrink-0 mt-0.5" data-id="element-190" data-mp="13mhylez4eg9e" />
                  <span data-id="element-191" data-mp="17od6x3fgxtwo">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>

        <p className="text-center text-[#081632] font-bold mb-4" data-id="element-192" data-mp="2s5vv9nf6xq95">
          Swipe through real pages from Judo Coach Ready
        </p>

        <div className="relative" data-id="element-193" data-mp="2o7y4ig5ruw3n">
          <div className="relative bg-[#F5F7FB] border border-[#D89B16]/40 rounded-2xl shadow-sm shadow-[#1E4ED8]/10 overflow-hidden" role="group" aria-roledescription="carousel" aria-label="Product page previews" data-id="element-194" data-mp="2jebyv7y0zshc">
            <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-195" data-mp="24hkz2xkfwer7">
              <motion.div key={slide.src} custom={direction} initial={{
              opacity: 0,
              x: direction > 0 ? 40 : -40
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction > 0 ? -40 : 40
            }} transition={{
              duration: 0.25,
              ease: easeOut
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.15} onDragEnd={(_, info) => {
              if (info.offset.x < -60) paginate(1);else if (info.offset.x > 60) paginate(-1);
            }} className="p-4 sm:p-8 cursor-zoom-in touch-pan-y" onClick={() => setIsZoomed(true)} aria-roledescription="slide" aria-label={`${index + 1} of ${gallerySlides.length}: ${slide.title}`} data-id="element-196" data-mp="24jpcq7854s4o">
                <img src={slide.src} alt={slide.alt} draggable={false} className="w-full h-auto max-h-[70vh] object-contain mx-auto select-none rounded-lg" data-id="element-197" data-mp="23rfs86m9smvq" />
              </motion.div>
            </AnimatePresence>

            <button type="button" onClick={() => setIsZoomed(true)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#081632]/90 text-[#D89B16] flex items-center justify-center hover:bg-[#081632] transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D89B16]" aria-label="View image larger" data-id="element-198" data-mp="1yscyhibqq332">
              <Maximize2 size={18} data-id="element-199" data-mp="1g5vp90gczdpq" />
            </button>
          </div>

          <button type="button" onClick={() => paginate(-1)} className="absolute left-0 sm:-left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#081632] text-[#D89B16] border border-[#D89B16]/50 shadow-lg flex items-center justify-center hover:bg-[#1E4ED8] hover:text-white transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D89B16]" aria-label="Previous image" data-id="element-200" data-mp="1g42gnfm6qjf3">
            <ChevronLeft size={24} data-id="element-201" data-mp="1tybu86stllta" />
          </button>
          <button type="button" onClick={() => paginate(1)} className="absolute right-0 sm:-right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#081632] text-[#D89B16] border border-[#D89B16]/50 shadow-lg flex items-center justify-center hover:bg-[#1E4ED8] hover:text-white transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D89B16]" aria-label="Next image" data-id="element-202" data-mp="27wjog6yh290r">
            <ChevronRight size={24} data-id="element-203" data-mp="2pnkdc5aate4l" />
          </button>
        </div>

        <p className="text-center text-[#081632] font-bold text-sm sm:text-base mt-6" data-id="element-204" data-mp="2ppwz6tj6g8mx">{slide.title}</p>

        <div className="flex justify-center items-center gap-3 mt-4" data-id="element-205" data-mp="2trs0vz50so18">
          {gallerySlides.map((item, dotIndex) => <button key={item.src} type="button" onClick={() => goTo(dotIndex)} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E4ED8] ${dotIndex === index ? 'w-8 bg-[#D89B16]' : 'w-2.5 bg-[#081632]/25 hover:bg-[#081632]/40'}`} aria-label={`Go to image ${dotIndex + 1}: ${item.title}`} aria-current={dotIndex === index} data-id="element-206" data-mp="33nv5rcr9b0rv" data-mpi={dotIndex} />)}
        </div>

        <p className="text-center text-lg font-serif font-bold text-[#081632] mt-10" data-id="element-207" data-mp="3iqmtibbh0ib9">
          Real pages. Real coaching tools.{' '}
          <span className="text-[#D89B16]" data-id="element-208" data-mp="3dsl9no680eyq">A system built to be used — not just read.</span>
        </p>
      </div>

      <AnimatePresence data-id="element-209" data-mp="eb3ofonpapgg">
        {isZoomed && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: easeOut
      }} className="fixed inset-0 z-50 bg-[#081632]/95 flex items-center justify-center p-4 overflow-auto" role="dialog" aria-modal="true" aria-label={slide.title} onClick={() => setIsZoomed(false)} data-id="element-210" data-mp="aatk0gfk473p">
            <button type="button" onClick={() => setIsZoomed(false)} className="fixed top-4 right-4 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D89B16]" aria-label="Close enlarged image" data-id="element-211" data-mp="2l19xvgmz9e0j">
              <X size={22} data-id="element-212" data-mp="k2pwhk4zdlzd" />
            </button>
            <motion.img initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.97
        }} transition={{
          duration: 0.2,
          ease: easeOut
        }} src={slide.src} alt={slide.alt} className="max-w-full md:max-w-3xl h-auto rounded-xl shadow-2xl" onClick={event => event.stopPropagation()} data-id="element-213" data-mp="17pxztwiumkyr" />
          </motion.div>}
      </AnimatePresence>
    </section>;
}